/**
 * In-session resumable-upload store — the single owner of how interrupted
 * uploads are persisted in `localStorage`. The encoding (one JSON blob keyed by
 * a file fingerprint) is this module's secret; callers only ever see
 * `ResumeRecord` values.
 *
 * A record is written at init time carrying the full init response (part signed
 * URLs and all), then each part is marked done as it uploads. Because init
 * returns 72h-TTL presigned URLs, a record whose URLs have not aged past that
 * TTL can be finished WITHOUT a fresh init: re-PUT only the parts not yet in
 * `doneParts`, reuse the stored ETags for the rest, then final. The record is
 * cleared on a successful final.
 *
 * Every read and write is wrapped in try/catch so private-mode / disabled
 * storage degrades to "no persistence" instead of crashing the page.
 */

import type { InitItem, MediaUploadAccess } from "../_types/media-upload";

const STORAGE_KEY = "birdwatch:media-upload:resume";

/** Presigned part URLs from init live 72h — a record is resumable until then. */
export const RESUME_TTL_MS = 72 * 60 * 60 * 1000;

export interface ResumeRecord {
  /** `access:name:size:lastModified` — stable across a page reload for the same file and route pair. */
  fingerprint: string;
  /** The route pair whose init this is — it decides the relay channel processing is announced on. */
  access: MediaUploadAccess;
  /** The full init response item, including every part's signed URL. */
  init: InitItem;
  /** PartNumbers already PUT successfully. */
  doneParts: number[];
  /** PartNumber (as string) → ETag, for the parts in `doneParts`. */
  etags: Record<string, string>;
  createdAt: number;
}

/**
 * Stable per-upload identity: the same file re-picked after a reload matches — on the same
 * route pair only. Media announces a signed-in init's processing on a private channel that a
 * signed-out page cannot hear, so a record must never be reused across the pair.
 */
export function fingerprintFile(file: File, access: MediaUploadAccess): string {
  return `${access}:${file.name}:${file.size}:${file.lastModified}`;
}

/** True while the record's presigned URLs are still within their 72h TTL. */
export function isFresh(record: ResumeRecord, now = Date.now()): boolean {
  return now - record.createdAt < RESUME_TTL_MS;
}

/** Milliseconds until the record's presigned URLs expire (never negative). */
export function msUntilExpiry(record: ResumeRecord, now = Date.now()): number {
  return Math.max(0, record.createdAt + RESUME_TTL_MS - now);
}

function readAll(): Record<string, ResumeRecord> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    // Records written before `access` existed are dropped: nothing says which route pair minted
    // their init, and a guess subscribes the wrong channel. Their upload simply starts fresh.
    return Object.fromEntries(
      Object.entries(
        parsed as Record<string, Partial<ResumeRecord> | null>,
      ).filter(
        (entry): entry is [string, ResumeRecord] =>
          entry[1]?.access === "authed" || entry[1]?.access === "public",
      ),
    );
  } catch {
    return {};
  }
}

function writeAll(records: Record<string, ResumeRecord>): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
  } catch {
    // Private mode / disabled storage — resume simply won't survive a reload.
  }
}

/** All still-fresh records, newest first; stale ones are pruned on read. */
export function loadResumeRecords(): ResumeRecord[] {
  const all = readAll();
  const fresh: Record<string, ResumeRecord> = {};
  const out: ResumeRecord[] = [];
  for (const record of Object.values(all)) {
    if (isFresh(record)) {
      fresh[record.fingerprint] = record;
      out.push(record);
    }
  }
  if (Object.keys(fresh).length !== Object.keys(all).length) writeAll(fresh);
  return out.sort((a, b) => b.createdAt - a.createdAt);
}

/** The fresh record for a fingerprint, or null (stale records don't match). */
export function findResumeRecord(fingerprint: string): ResumeRecord | null {
  const record = readAll()[fingerprint];
  return record && isFresh(record) ? record : null;
}

/** Insert or replace the record for its fingerprint. */
export function saveResumeRecord(record: ResumeRecord): void {
  const all = readAll();
  all[record.fingerprint] = record;
  writeAll(all);
}

/** Record one part as uploaded (idempotent: re-marking the same part is a no-op). */
export function markPartDone(
  fingerprint: string,
  partNumber: number,
  etag: string,
): void {
  const all = readAll();
  const record = all[fingerprint];
  if (!record) return;
  const doneParts = record.doneParts.includes(partNumber)
    ? record.doneParts
    : [...record.doneParts, partNumber];
  all[fingerprint] = {
    ...record,
    doneParts,
    etags: { ...record.etags, [String(partNumber)]: etag },
  };
  writeAll(all);
}

/** Drop the record for a fingerprint (called after a successful final). */
export function clearResumeRecord(fingerprint: string): void {
  const all = readAll();
  if (!(fingerprint in all)) return;
  delete all[fingerprint];
  writeAll(all);
}
